/**
 * Admin — AI Prompt Editor (/admin/prompts)
 *
 * Three cards, one per AI prompt type. Each card has:
 *   - Friendly name + description
 *   - User prompt textarea with click-to-insert variable chips
 *   - Pinned JSON output reminder (reflection + scrape_url only)
 *   - Advanced collapse for system prompt
 *   - Save + Reset to default buttons
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { AppLayout } from '@/layouts/AppLayout';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

// ── Types ─────────────────────────────────────────────────────────────────────

type PromptType = 'reflection' | 'annual_plan' | 'scrape_url';

interface PromptConfig {
  type: PromptType;
  hasCustom: boolean;
  systemPrompt: string;
  userPromptTemplate: string;
  defaultSystemPrompt: string;
  defaultUserPromptTemplate: string;
  updatedAt: string | null;
}

// ── Static metadata ───────────────────────────────────────────────────────────

const PROMPT_META: Record<PromptType, {
  title: string;
  description: string;
  variables: { key: string; label: string }[];
  jsonFooter: string | null;
}> = {
  reflection: {
    title: 'Reflection Writer',
    description: 'Generates the outcome + review draft when a member clicks "Generate AI draft" on an activity.',
    variables: [
      { key: 'activityTitle',       label: 'Activity title'  },
      { key: 'activityType',        label: 'Activity type'   },
      { key: 'activityHours',       label: 'Hours'           },
      { key: 'activityDescription', label: 'Notes'           },
    ],
    jsonFooter: '{"outcome": "...", "review": "..."}',
  },
  annual_plan: {
    title: 'Annual CPD Plan',
    description: 'Generates the personalised CPD plan during member onboarding.',
    variables: [
      { key: 'currentYear',         label: 'Year'             },
      { key: 'yearsRegistered',     label: 'Years registered' },
      { key: 'practiceType',        label: 'Practice type'    },
      { key: 'specialInterests',    label: 'Special interests' },
      { key: 'weakAreas',           label: 'Areas to improve' },
      { key: 'goalsNextYear',       label: 'Goals'            },
      { key: 'culturalSafetyFocus', label: 'Cultural safety'  },
    ],
    jsonFooter: null,
  },
  scrape_url: {
    title: 'Activity from URL',
    description: 'Generates a draft activity when a member pastes a URL (conference, webinar, article).',
    variables: [
      { key: 'pageContent', label: 'Page content' },
      { key: 'sourceUrl',   label: 'Source URL'   },
    ],
    jsonFooter: '{"title": "...", "description": "...", "hours": 0, "outcome": "...", "review": "..."}',
  },
};

const PROMPT_ORDER: PromptType[] = ['reflection', 'annual_plan', 'scrape_url'];

// ── Variable chip insertion ───────────────────────────────────────────────────

function insertVariable(
  ref: React.RefObject<HTMLTextAreaElement | null>,
  varKey: string,
  value: string,
  onChange: (v: string) => void,
): void {
  const el = ref.current;
  if (!el) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const insert = `{{${varKey}}}`;
  onChange(value.slice(0, start) + insert + value.slice(end));
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(start + insert.length, start + insert.length);
  });
}

// ── Single prompt card ────────────────────────────────────────────────────────

function PromptCard({ config, onSaved }: { config: PromptConfig; onSaved: (updated: PromptConfig) => void }): React.ReactElement {
  const meta = PROMPT_META[config.type];
  const [userPrompt, setUserPrompt] = useState(config.userPromptTemplate);
  const [systemPrompt, setSystemPrompt] = useState(config.systemPrompt);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userPromptRef = useRef<HTMLTextAreaElement | null>(null);
  const systemPromptRef = useRef<HTMLTextAreaElement | null>(null);
  const [activeRef, setActiveRef] = useState<React.RefObject<HTMLTextAreaElement | null>>(userPromptRef);

  const isDirty = userPrompt !== config.userPromptTemplate || systemPrompt !== config.systemPrompt;

  const handleSave = useCallback(async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/content/prompts/${config.type}`, { systemPrompt, userPromptTemplate: userPrompt });
      setSavedFlash(true);
      setTimeout(() => { setSavedFlash(false); }, 2000);
      onSaved({ ...config, systemPrompt, userPromptTemplate: userPrompt, hasCustom: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [config, systemPrompt, userPrompt, onSaved]);

  const handleReset = useCallback((): void => {
    if (!confirm('Reset to the built-in default prompt? Your custom changes will be lost.')) return;
    setUserPrompt(config.defaultUserPromptTemplate);
    setSystemPrompt(config.defaultSystemPrompt);
  }, [config.defaultSystemPrompt, config.defaultUserPromptTemplate]);

  return (
    <div className="bg-[var(--stitch-surface-container-lowest)] rounded-2xl border border-[var(--stitch-outline-variant)] overflow-hidden">
      {/* Card header */}
      <div className="px-6 pt-5 pb-4 border-b border-[var(--stitch-outline-variant)] flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="material-symbols-outlined text-[20px] text-[var(--stitch-primary)]">auto_awesome</span>
            <h2 className="stitch-font-display text-[18px] font-bold text-[var(--stitch-primary)] tracking-tight">{meta.title}</h2>
            <span className={`stitch-font-label text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider ${
              config.hasCustom
                ? 'bg-[var(--stitch-primary-container)] text-[var(--stitch-on-primary-container)]'
                : 'bg-[var(--stitch-surface-container-high)] text-[var(--stitch-on-surface-variant)]'
            }`}>
              {config.hasCustom ? 'Custom' : 'Default'}
            </span>
          </div>
          <p className="stitch-font-body text-[13px] text-[var(--stitch-on-surface-variant)]">{meta.description}</p>
        </div>
        {config.updatedAt && (
          <p className="stitch-font-label text-[11px] text-[var(--stitch-on-surface-variant)] shrink-0 mt-1">
            Saved {new Date(config.updatedAt).toLocaleDateString('en-NZ')}
          </p>
        )}
      </div>

      <div className="px-6 py-5 flex flex-col gap-5">
        {/* User prompt */}
        <div>
          <label className="stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider mb-2 block">
            Prompt
          </label>
          <textarea
            ref={userPromptRef}
            rows={10}
            value={userPrompt}
            onChange={e => { setUserPrompt(e.target.value); }}
            onFocus={() => { setActiveRef(userPromptRef); }}
            className="w-full rounded-xl border border-[var(--stitch-outline-variant)] bg-[var(--stitch-surface)] px-4 py-3 stitch-font-body text-[14px] text-[var(--stitch-on-surface)] resize-y focus:outline-none focus:ring-2 focus:ring-[var(--stitch-primary)] focus:border-transparent"
          />
          {/* Pinned JSON footer */}
          {meta.jsonFooter && (
            <div className="mt-1 px-4 py-2 rounded-lg bg-[var(--stitch-surface-container-high)] border border-[var(--stitch-outline-variant)]">
              <p className="stitch-font-label text-[11px] text-[var(--stitch-on-surface-variant)] mb-0.5 uppercase tracking-wider">Required JSON output format — do not remove from prompt</p>
              <code className="text-[12px] text-[var(--stitch-on-surface-variant)] font-mono">
                Respond with valid JSON only (no markdown fences):{' '}
                <span className="text-[var(--stitch-primary)]">{meta.jsonFooter}</span>
              </code>
            </div>
          )}
        </div>

        {/* Variable chips */}
        <div>
          <p className="stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider mb-2">
            Insert variable at cursor
          </p>
          <div className="flex flex-wrap gap-2">
            {meta.variables.map(v => (
              <button
                key={v.key}
                type="button"
                onClick={() => { insertVariable(activeRef, v.key, activeRef === userPromptRef ? userPrompt : systemPrompt, activeRef === userPromptRef ? setUserPrompt : setSystemPrompt); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--stitch-outline-variant)] bg-[var(--stitch-surface-container-low)] hover:bg-[var(--stitch-primary-container)] hover:border-[var(--stitch-primary)] transition-colors stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] hover:text-[var(--stitch-on-primary-container)]"
              >
                <span className="material-symbols-outlined text-[14px]">add</span>
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {/* Advanced — system prompt */}
        <div>
          <button
            type="button"
            onClick={() => { setAdvancedOpen(o => !o); }}
            className="flex items-center gap-1.5 stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider hover:text-[var(--stitch-primary)] transition-colors"
          >
            <span className={`material-symbols-outlined text-[16px] transition-transform ${advancedOpen ? 'rotate-90' : ''}`}>chevron_right</span>
            Advanced — System prompt
          </button>
          {advancedOpen && (
            <div className="mt-3">
              <p className="stitch-font-body text-[12px] text-[var(--stitch-on-surface-variant)] mb-2">
                Sets Claude's role and tone. Rarely needs changing.
              </p>
              <textarea
                ref={systemPromptRef}
                rows={6}
                value={systemPrompt}
                onChange={e => { setSystemPrompt(e.target.value); }}
                onFocus={() => { setActiveRef(systemPromptRef); }}
                className="w-full rounded-xl border border-[var(--stitch-outline-variant)] bg-[var(--stitch-surface)] px-4 py-3 stitch-font-body text-[13px] text-[var(--stitch-on-surface)] resize-y focus:outline-none focus:ring-2 focus:ring-[var(--stitch-primary)] focus:border-transparent"
              />
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <p role="alert" className="text-sm text-red-600">{error}</p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={handleReset}
            className="stitch-font-label text-[12px] uppercase tracking-wider text-[var(--stitch-on-surface-variant)] hover:text-[var(--stitch-primary)] transition-colors"
          >
            Reset to default
          </button>
          <button
            type="button"
            disabled={saving || !isDirty}
            onClick={() => { void handleSave(); }}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] stitch-font-label text-[13px] uppercase tracking-wider font-bold hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            {savedFlash ? (
              <><span className="material-symbols-outlined text-[16px]">check</span> Saved</>
            ) : saving ? (
              <><span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span> Saving…</>
            ) : (
              'Save changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function AdminPromptsContent(): React.ReactElement {
  const [prompts, setPrompts] = useState<PromptConfig[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ prompts: PromptConfig[] }>('/api/content/prompts')
      .then(res => { setPrompts(res.prompts); })
      .catch(err => { setError(err instanceof Error ? err.message : 'Failed to load prompts'); });
  }, []);

  const handleSaved = useCallback((updated: PromptConfig): void => {
    setPrompts(prev => prev?.map(p => p.type === updated.type ? updated : p) ?? null);
  }, []);

  return (
    <div className="stitch-font-body text-[var(--stitch-on-surface)]">
      <div className="mb-8">
        <h1 className="stitch-font-display text-[30px] md:text-[34px] font-bold text-[var(--stitch-primary)] tracking-tight">
          AI Prompts
        </h1>
        <p className="stitch-font-body text-[14px] text-[var(--stitch-on-surface-variant)] mt-1">
          Admin — customise the instructions Claude uses to generate content for your members.
        </p>
      </div>

      {error && (
        <div role="alert" className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {!prompts && !error && (
        <div className="flex items-center justify-center py-24">
          <span className="material-symbols-outlined animate-spin text-[var(--stitch-primary)] text-4xl">progress_activity</span>
        </div>
      )}

      {prompts && (
        <div className="flex flex-col gap-6">
          {PROMPT_ORDER.map(type => {
            const config = prompts.find(p => p.type === type);
            if (!config) return null;
            return <PromptCard key={type} config={config} onSaved={handleSaved} />;
          })}
        </div>
      )}
    </div>
  );
}

export function AdminPromptsPage(): React.ReactElement {
  const { user } = useAuth();
  if (!user?.isAdmin) {
    return (
      <AppLayout currentRoute="/admin/prompts">
        <p className="text-[var(--text-secondary)]">Access denied.</p>
      </AppLayout>
    );
  }
  return (
    <AppLayout currentRoute="/admin/prompts">
      <AdminPromptsContent />
    </AppLayout>
  );
}
